import is from '@sindresorhus/is';
import type { RefObject } from 'react';

import { computeTargetScrollLeft, type TileScrollAlign } from './scroll-helpers';
import type { LayoutLifecycleCallback } from './types';

export type ScrollToTileOptions = {
    readonly align?: TileScrollAlign;
    readonly behavior?: ScrollBehavior;
    readonly padding?: number;
};

type ResolvedOptions = Required<ScrollToTileOptions>;

const DEFAULTS: ResolvedOptions = {
    align: 'auto',
    behavior: 'smooth',
    padding: 8,
};

const TILE_REGISTRATION_TIMEOUT_FRAMES = 30;
const SETTLE_TIMEOUT_MS = 1_000;

type ScrollRequest = {
    cancelled: boolean;
};

export type ScrollCoordinatorDeps = {
    readonly containerRef: RefObject<HTMLDivElement | null>;
    readonly tileBoxRefs: RefObject<Map<string, HTMLElement>>;
    readonly activeAnimationsRef: RefObject<Set<string>>;
    readonly subscribe: (callback: LayoutLifecycleCallback) => () => void;
    // Per-tile widths to reserve at the right edge of the scrollport (for
    // sticky-right tiles like docks). The target tile's own reservation is
    // ignored so scrolling to it works the way callers expect.
    readonly endReservationsRef: RefObject<Map<string, number>>;
};

export type ScrollCoordinator = {
    readonly scrollToTile: (id: string, options?: ScrollToTileOptions) => Promise<void>;
};

const nextFrame = (): Promise<void> =>
    new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
    });

// Single-pending-request queue: newer request cancels older before dispatch.
export const createScrollCoordinator = (deps: ScrollCoordinatorDeps): ScrollCoordinator => {
    let pending: ScrollRequest | null = null;

    const waitForSettled = (): Promise<void> => {
        if (deps.activeAnimationsRef.current.size === 0) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve) => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            const unsubscribe = deps.subscribe((event) => {
                if (event.type !== 'layoutSettled') {
                    return;
                }

                if (is.number(timeoutId)) {
                    clearTimeout(timeoutId);
                }

                unsubscribe();
                resolve();
            });

            timeoutId = setTimeout(() => {
                unsubscribe();
                resolve();
            }, SETTLE_TIMEOUT_MS);
        });
    };

    const waitForTileBox = async (id: string, request: ScrollRequest): Promise<HTMLElement | null> => {
        let tile = deps.tileBoxRefs.current.get(id) ?? null;
        let retries = 0;

        while (is.null(tile) && retries < TILE_REGISTRATION_TIMEOUT_FRAMES && !request.cancelled) {
            await nextFrame();
            tile = deps.tileBoxRefs.current.get(id) ?? null;
            retries += 1;
        }

        return tile;
    };

    const execute = async (id: string, options: ResolvedOptions, request: ScrollRequest): Promise<void> => {
        const container = deps.containerRef.current;

        if (is.null(container) || request.cancelled) {
            return;
        }

        const tile = await waitForTileBox(id, request);

        if (is.null(tile) || request.cancelled) {
            return;
        }

        await waitForSettled();

        if (request.cancelled) {
            return;
        }

        // Extra frame so rects reflect post-settle layout.
        await nextFrame();

        if (request.cancelled || is.null(deps.containerRef.current)) {
            return;
        }

        let endReservedWidth = 0;

        for (const [reservedId, width] of deps.endReservationsRef.current.entries()) {
            if (reservedId === id) {
                continue;
            }

            endReservedWidth += width;
        }

        const target = computeTargetScrollLeft(tile, container, options.align, options.padding, endReservedWidth);

        if (is.null(target)) {
            return;
        }

        container.scrollTo({
            behavior: options.behavior,
            left: target,
        });
    };

    const scrollToTile = (id: string, options?: ScrollToTileOptions): Promise<void> => {
        if (!is.null(pending)) {
            pending.cancelled = true;
        }

        const request: ScrollRequest = { cancelled: false };
        pending = request;

        const resolved: ResolvedOptions = {
            align: options?.align ?? DEFAULTS.align,
            behavior: options?.behavior ?? DEFAULTS.behavior,
            padding: options?.padding ?? DEFAULTS.padding,
        };

        return execute(id, resolved, request).finally(() => {
            if (pending === request) {
                pending = null;
            }
        });
    };

    return { scrollToTile };
};
